import { geoForkRise, geoForkRun, geoHTT, geoTrail, geoSeatedFit } from "@/modules/computeGeo.js";
import { copy, sinD, cosD, round, acos_fpe } from "@/modules/utility.js";

//------------------------------------------------------------
// Distance between two points
export function magnitude(p1, p2) {
	return Math.sqrt((p1[0] - p2[0]) ** 2 + (p1[1] - p2[1]) ** 2);
}

// Find angle with respect to x-axis from p1 to p2
export function theta_x(p1, p2) {
	const r = magnitude(p1, p2);
	const theta = r === 0 ? 0 : acos_fpe((p2[0] - p1[0]) / r)

	// check if angle is in 3rd or 4th quadrant and adjust and return
	// output range must be -pi to pi to match the 2*atan(x) function 
	// that is used to get link angles
	return p2[1] < p1[1] ? -theta : theta;
}

//Subtract two points element wise
export function subLi(p1, p2) { return [p1[0] - p2[0], p1[1] - p2[1]]; }

//Move point by a displacement vector
export function movePoint(p1, v_translate) {
	return [p1[0] + v_translate[0], p1[1] + v_translate[1]]
}

//Rotate point about point
export function rotPoint(p1, p_axis, theta) {
	const v = [p1[0] - p_axis[0], p1[1] - p_axis[1]];
	return [
		v[0] * Math.cos(theta) - v[1] * Math.sin(theta) + p_axis[0],
		v[0] * Math.sin(theta) + v[1] * Math.cos(theta) + p_axis[1]
	];
}

//Rotate then translate point in 2D space
export function rotMovePoint(p1, p_axis, theta, v_translate) {
	const v = [p1[0] - p_axis[0], p1[1] - p_axis[1]]
	return [v[0] * Math.cos(theta) - v[1] * Math.sin(theta) + p_axis[0] + v_translate[0],
		v[0] * Math.sin(theta) + v[1] * Math.cos(theta) + p_axis[1] + v_translate[1]
	]
}

//Translate then rotate point in 2D space
export function moveRotPoint(p1, p_axis, theta, v_translate) {
	return rotPoint(movePoint(p1, v_translate), p_axis, theta)
}

//Find the intersect of two circles closest to p_ref
export function circleIntersect(p0, p1, r0, r1, p_ref = [0, 0]) {
	// Equations from:
	// http://www.ambrsoft.com/TrigoCalc/Circles2/circle2intersection/CircleCircleIntersection.htm

	const D = magnitude(p0, p1);
	const a = p0[0];
	const b = p0[1];
	const c = p1[0];
	const d = p1[1];

	const x_base = (a + c) / 2 + (c - a) * (r0 * r0 - r1 * r1) / (2 * D * D);
	const y_base = (b + d) / 2 + (d - b) * (r0 * r0 - r1 * r1) / (2 * D * D);
	let theta = (D + r0 + r1) * (D + r0 - r1) * (D - r0 + r1) * (-D + r0 + r1);
	if (theta < 0) { return [NaN, NaN]; }

	theta = Math.sqrt(theta) / 4;

	const x0 = x_base + 2 * (b - d) / (D * D) * theta
	const x1 = x_base - 2 * (b - d) / (D * D) * theta
	const y0 = y_base - 2 * (a - c) / (D * D) * theta
	const y1 = y_base + 2 * (a - c) / (D * D) * theta

	if (magnitude(p_ref, [x0, y0]) < magnitude(p_ref, [x1, y1])) {
		return [x0, y0];
	} else {
		return [x1, y1];
	}
}

//Get the shortest distance from point to a line
export function pointToLine(p1, p2, p3) {
	//Finds distance from p3 to a line of p1-p2
	//uses formula from http://paulbourke.net/geometry/pointlineplane/

	//check for coincident points
	if (p1[0] == p2[0] && p1[1] == p2[1]) {
		return magnitude(p3, p2);
	} else {
		const segLength = magnitude(p1, p2);
		const u = ((p3[0] - p1[0]) * (p2[0] - p1[0]) + (p3[1] - p1[1]) * (p2[1] - p1[1])) /
			(segLength * segLength);
		const p_n = [p1[0] + u * (p2[0] - p1[0]), p1[1] + u * (p2[1] - p1[1])];
		return magnitude(p3, p_n)
	}
}

//Find the difference between two angles
//Accounts for negative angles and angles > 2 PI
function diff_angle(a1, a2) {
	const twoPi = 2 * Math.PI;
	a1 = a1 % twoPi;
	a2 = a2 % twoPi;

	a1 = a1 < 0 ? a1 + twoPi : a1;
	a2 = a2 < 0 ? a2 + twoPi : a2;

	return Math.abs(a1 - a2);
}

//Solve four bar linkage angles with forward loop closure
export function linkAngles_f(r0, r1, r2, r3, t0, t1, t2, t3, d_t1) {
	const twoPi = 2 * Math.PI
	t1 = t1 + d_t1

	const c1 = r0 * Math.cos(t0) + r1 * Math.cos(t1); // real constant
	const c2 = r0 * Math.sin(t0) + r1 * Math.sin(t1); // imag constant

	// r3 on one side, sum and square
	const c3 = (r3 * r3 - c2 * c2 - c1 * c1 - r2 * r2) / (2 * r2);

	const a = c3 + c1;
	const b = -2 * c2;
	const c = c3 - c1;

	// check for imaginary values indicating linkage lock
	if ((b * b - 4 * a * c) > 0) {
		const root0 = (-b - Math.sqrt(b * b - 4 * a * c)) / (2 * a);
		const root1 = (-b + Math.sqrt(b * b - 4 * a * c)) / (2 * a);

		t2 = t2 % twoPi
		const t2a = 2 * Math.atan(root0) % twoPi;
		const t2b = 2 * Math.atan(root1) % twoPi;

		// Use reference angle to determine which linkage state to use
		t2 = diff_angle(t2, t2a) < diff_angle(t2, t2b) ? t2a : t2b;

		let t3 = acos_fpe(-(c1 + Math.cos(t2) * r2) / r3);

		if (c2 + Math.sin(t2) * r2 > 0) { t3 = 2 * Math.PI - t3; }

		return [t1, t2, t3, false];
	} else {
		return [0, 0, 0, true];
	}
}


//Solve four bar linkage angles with reverse loop closure
export function linkAngles_r(r0, r1_in, r2, r3_in, t0, t1_in, t2, t3_in, d_t3) {
	const twoPi = 2 * Math.PI
	const pi = Math.PI
	// Loop closure in opposite direction (0 -> 3 -> 2 -> 1 -> 0)
	t0 = t0 + pi
	let t1 = t3_in + pi + d_t3
	t2 = t2 + pi
	let t3 = t1_in + pi

	let r3 = r1_in
	let r1 = r3_in

	const c1 = r0 * Math.cos(t0) + r1 * Math.cos(t1); // real constant
	const c2 = r0 * Math.sin(t0) + r1 * Math.sin(t1); // imag constant

	// r3 on one side, sum and square
	const c3 = (r3 * r3 - c2 * c2 - c1 * c1 - r2 * r2) / (2 * r2);

	const a = c3 + c1;
	const b = -2 * c2;
	const c = c3 - c1;

	// check for imaginary values indicating linkage lock
	if ((b * b - 4 * a * c) > 0) {
		const root0 = (-b - Math.sqrt(b * b - 4 * a * c)) / (2 * a);
		const root1 = (-b + Math.sqrt(b * b - 4 * a * c)) / (2 * a);

		t2 = t2 % twoPi
		const t2a = 2 * Math.atan(root0) % twoPi;
		const t2b = 2 * Math.atan(root1) % twoPi;

		// Use reference angle to determine which linkage state to use
		t2 = diff_angle(t2, t2a) < diff_angle(t2, t2b) ? t2a : t2b;

		t3 = acos_fpe(-(c1 + Math.cos(t2) * r2) / r3);

		if (c2 + Math.sin(t2) * r2 > 0) { t3 = twoPi - t3; }

		return [t3 - pi, t2 - pi, t1 - pi];
	} else {
		return [NaN, NaN, NaN];
	}
}

//Select either forward or reverse loops closure and return d_t1, d_t2, d_t3
function computeFourBar(r0, r1, r2, r3, t0, t1, t2, t3, d_t1, d_t3, loopDir) {
	let angles = [];
	if (!loopDir) {
		angles = linkAngles_f(r0, r1, r2, r3, t0, t1, t2, t3, d_t1);
		loopDir = angles[3];
	}
	if (loopDir) {
		angles = linkAngles_r(r0, r1, r2, r3, t0, t1, t2, t3, d_t3);
	}

	return [angles[0] - t1, angles[1] - t2, angles[2] - t3, loopDir]
}

//Output new points after a rotation
export function fourBarSolve(p0, p1, p2, p3, p4, p5, p6, p7, p8, p9, p10,
	p11, p12, p13, p14, p15, p16, p17, p18, p19,
	solverInfo, res, d_t1, d_t3 = 0, loopDir = false) {
	const linkageType = solverInfo.linkageType;

	const bbAttachedTo = solverInfo.bbAttachedTo;
	const wheelAttachedTo = solverInfo.wheelAttachedTo;
	const shock1AttachedTo = solverInfo.shock1AttachedTo;
	const shock2AttachedTo = solverInfo.shock2AttachedTo;

	const link4AttachedTo = solverInfo.link4AttachedTo;
	const link5AttachedTo = solverInfo.link5AttachedTo;
	const brakeAttachedTo = solverInfo.brakeAttachedTo;

	const idlerAttachedTo = solverInfo.idlerAttachedTo;
	const DAQ1AttachedTo = solverInfo.DAQ1AttachedTo;
	const DAQ2AttachedTo = solverInfo.DAQ2AttachedTo;

	const usesBrakeArm = solverInfo.usesBrakeArm;
	const usesPullShock = solverInfo.usesPullShock;

	let d_t2 = 0;
	if (usesPullShock) {
		d_t1 = -d_t1;
		d_t3 = -d_t3;
	}

	let r0 = magnitude(p4, p7);
	let r1 = magnitude(p5, p4);
	let r2 = magnitude(p6, p5);
	let r3 = magnitude(p7, p6);

	let r4 = magnitude(p8, p9);
	let r5 = magnitude(p9, p10);

	let r6 = magnitude(p1, p12);
	let r7 = magnitude(p12, p13);

	let shockLength = magnitude(p2, p3);

	// Get initial angles
	let t0 = theta_x(p7, p4);
	let t1 = theta_x(p4, p5);
	let t2 = theta_x(p5, p6);
	let t3 = theta_x(p6, p7);

	let t4 = theta_x(p8, p9);
	let t5 = theta_x(p9, p10);

	let p5_new = [];
	let p6_new = [];
	let p8_new = [];
	let p9_new = [];
	let p10_new = [];
	let p12_new = [];
	let d_p5 = [0, 0]
	let d_p8 = [0, 0];
	let d_p10 = [0, 0];

	let d_t4 = 0;
	let d_t5 = 0;

	//Single Pivot must be handled without solver
	if (linkageType > 0) {
		[d_t1, d_t2, d_t3, loopDir] = computeFourBar(r0, r1, r2, r3, t0, t1, t2, t3, d_t1, d_t3, loopDir);
	}

	if (linkageType > 0) {
		p5_new = rotPoint(p5, p4, d_t1)
		d_p5 = subLi(p5_new, p5)
		p6_new = rotMovePoint(p6, p5, d_t2, d_p5)
	} else {
		p5_new = p5
		p6_new = p6
	}

	if (linkageType == 2) {
		// find p8 based on link attachment-------------------------------
		if (link4AttachedTo == 0) {
			p8_new = p8;
			d_p8 = [0, 0];
		} else if (link4AttachedTo == 1) {
			p8_new = rotPoint(p8, p4, d_t1);
			d_p8 = subLi(p8_new, p8);
		} else if (link4AttachedTo == 2) {
			p8_new = rotMovePoint(p8, p5, d_t2, d_p5);
			d_p8 = subLi(p8_new, p8);
		} else if (link4AttachedTo == 3) {
			p8_new = rotPoint(p8, p7, d_t3);
			d_p8 = subLi(p8_new, p8);
		}

		// find p10 based on link attachment------------------------------
		if (link5AttachedTo == 0) {
			p10_new = p10;
			d_p10 = [0, 0];
		} else if (link5AttachedTo == 1) {
			p10_new = rotPoint(p10, p4, d_t1);
			d_p10 = subLi(p10_new, p10);
		} else if (link5AttachedTo == 2) {
			p10_new = rotMovePoint(p10, p5, d_t2, d_p5);
			d_p10 = subLi(p10_new, p10);
		} else if (link5AttachedTo == 3) {
			p10_new = rotPoint(p10, p7, d_t3);
			d_p10 = subLi(p10_new, p10);
		}

		// find p9 based on two circle intersection and previous point
		p9_new = circleIntersect(p8_new, p10_new, r4, r5, p9);

		d_t4 = theta_x(p8_new, p9_new) - t4;
		d_t5 = theta_x(p9_new, p10_new) - t5;

	} else {
		p8_new = p8;
		p9_new = p9;
		p10_new = p10;
	}

	// Now use deltaT and deltaP to find all other points-------------
	const points = [p0, p1, p2, p3, p11, p13, p14, p15];
	const attachements = [
		bbAttachedTo,
		wheelAttachedTo,
		shock1AttachedTo,
		shock2AttachedTo,
		idlerAttachedTo,
		brakeAttachedTo,
		DAQ1AttachedTo,
		DAQ2AttachedTo
	];
	const points_new = [0, 0, 0, 0, 0, 0, 0, 0];

	for (let j = 0; j < points.length; j++) {
		if (attachements[j] == 0) {
			points_new[j] = points[j];
		} else if (attachements[j] == 1) {
			points_new[j] = rotPoint(points[j], p4, d_t1); // frame connection
		} else if (attachements[j] == 2) {
			points_new[j] = rotMovePoint(points[j], p5, d_t2, d_p5);
		} else if (attachements[j] == 3) {
			points_new[j] = rotPoint(points[j], p7, d_t3); // frame connection
		} else if (attachements[j] == 4) {
			points_new[j] = rotMovePoint(points[j], p8, d_t4, d_p8);
		} else if (attachements[j] == 5) {
			points_new[j] = rotMovePoint(points[j], p10, d_t5, d_p10);
		}
	}

	if (usesBrakeArm) {
		p12_new = circleIntersect(points_new[1], points_new[5], r6, r7, p12);
	} else {
		p12_new = p12;
	}

	const d_shock = shockLength - magnitude(points_new[2], points_new[3]);

	// Check if shock is extending or compressing----------------------
	let direction = 1;
	if (d_shock < 0) { direction = -direction; }
	if (usesPullShock) { direction = -direction; } //reverse again for pull shock

	let d_t6 = theta_x(points_new[1], p12_new) - theta_x(p1, p12);
	let d_t7 = theta_x(p12_new, points_new[5]) - theta_x(p12, p13);

	let d_p9 = subLi(p9_new, p9);
	let d_p1 = subLi(points_new[1], p1);
	let d_p12 = subLi(p12_new, p12);

	//Construct result list
	let resultList = [
		[d_t1, [0, 0]]
	];
	if (linkageType === 1) {
		resultList.push([d_t2, d_p5], [d_t3, [0, 0]]);
	}
	if (linkageType === 2) {
		resultList.push([d_t2, d_p5], [d_t3, [0, 0]], [d_t4, d_p8], [d_t5, d_p9]);
	}
	if (usesBrakeArm) {
		resultList.push([d_t6, d_p1], [d_t7, d_p12]);
	}

	p5 = p5_new
	p6 = p6_new
	p8 = p8_new
	p9 = p9_new
	p10 = p10_new
	p12 = p12_new

	p0 = points_new[0]
	p1 = points_new[1]
	p2 = points_new[2]
	p3 = points_new[3]

	p11 = points_new[4]
	p13 = points_new[5]
	p14 = points_new[5]
	p15 = points_new[6]

	const newPoints = [
		p0, p1, p2, p3, p4, p5, p6, p7, p8, p9,
		p10, p11, p12, p13, p14, p15, p16, p17, p18, p19
	];

	//Must make sure angles sit within 0 to 2PI
	const twoPi = 2 * Math.PI
	if (round(d_shock, 4) == 0) {
		d_t1 = 0;
		d_t3 = 0;
	} else {
		d_t1 = (d_t1 % twoPi) * res / d_shock;
		d_t3 = (d_t3 % twoPi) * res / d_shock;
	}

	return [resultList, newPoints, direction, d_t1, d_t3, loopDir];
}

//Graham scan to sort points
export function grahamScan(points) {
	//Credit to https://github.com/lovasoa/graham-fast
	//MIT License

	let i;
	// The enveloppe is the points themselves
	if (points.length <= 3) return points;

	// Find the pivot
	let pivot = points[0];
	for (i = 0; i < points.length; i++) {
		if (points[i][1] < pivot[1] || (points[i][1] === pivot[1] && points[i][0] < pivot[0]))
			pivot = points[i];
	}

	// Attribute an angle to the points
	for (i = 0; i < points.length; i++) {
		points[i]._graham_angle = Math.atan2(points[i][1] - pivot[1], points[i][0] - pivot[0]);
	}
	points.sort(function(a, b) {
		return a._graham_angle === b._graham_angle ?
			a[0] - b[0] :
			a._graham_angle - b._graham_angle
	});

	// Adding points to the result if they "turn left"
	let result = [points[0]],
		len = 1;
	for (i = 1; i < points.length; i++) {
		let a = result[len - 2],
			b = result[len - 1],
			c = points[i];
		while (
			(len === 1 && b[0] === c[0] && b[1] === c[1]) ||
			(len > 1 && (b[0] - a[0]) * (c[1] - a[1]) <= (b[1] - a[1]) * (c[0] - a[0]))) {
			len--;
			b = a;
			a = result[len - 2];
		}
		result[len++] = c;
	}
	result.length = len;
	return result;
}

// Common tangent of two circles
export function commonTan(c1, c2, r1, r2) {
	// Upper/Right external common tangent from circle 1 to circle 2
	// C1 should be left/above of C2 for upper/right
	// pt1 is on circle 1 and pt2 is on circle2
	const c_gamma = Math.atan2(c2[1] - c1[1], c2[0] - c1[0]);

	const c_beta = Math.asin((r1 - r2) / magnitude(c2, c1));

	const c_alpha = c_gamma - c_beta;

	const x3 = c1[0] - r1 * Math.cos(Math.PI / 2 - c_alpha);
	const y3 = c1[1] + r1 * Math.sin(Math.PI / 2 - c_alpha);
	const x4 = c2[0] - r2 * Math.cos(Math.PI / 2 - c_alpha);
	const y4 = c2[1] + r2 * Math.sin(Math.PI / 2 - c_alpha);

	const pt1 = [x3, y3];
	const pt2 = [x4, y4];
	return [pt1, pt2];
}


//----------------------------------------------------------------------
//GET INITIAL CONDITIONS FOR FLIP CHIP CONFIGS--------------------------

export function computeModLinkage(bikeData) {
	const chipResults = { error: false };
	const chipConfig = bikeData.solverInfo.chipConfigs[bikeData.solverInfo.selectedChip];

	//Copy parameter objs so that they can be changed later without impacting bike def
	let initPoints = copy(bikeData.points);
	let geo = copy(bikeData.geometry);
	let solverInfo = copy(bikeData.solverInfo);
	let drivetrainInfo = copy(bikeData.drivetrainInfo);
	//------------------------------------------------------------------------
	//Break out solverInfo parameters-----------------------------------------
	const linkageType = solverInfo.linkageType;
	const shockLength = magnitude(initPoints[2], initPoints[3]) + chipConfig.shockLength;

	const bbAttachedTo = solverInfo.bbAttachedTo;
	const wheelAttachedTo = solverInfo.wheelAttachedTo;
	const shock1AttachedTo = solverInfo.shock1AttachedTo;
	const shock2AttachedTo = solverInfo.shock2AttachedTo;

	const link4AttachedTo = solverInfo.link4AttachedTo;
	const link5AttachedTo = solverInfo.link5AttachedTo;
	const brakeAttachedTo = solverInfo.brakeAttachedTo;

	const idlerAttachedTo = solverInfo.idlerAttachedTo;
	const DAQ1AttachedTo = solverInfo.DAQ1AttachedTo;
	const DAQ2AttachedTo = solverInfo.DAQ2AttachedTo;

	const usesBrakeArm = solverInfo.usesBrakeArm;
	const usesPullShock = solverInfo.usesPullShock;

	const sliderRef = solverInfo.sliderRef;

	//Flip chip combinations MUST create a valid configuration at stock shock length

	//------------------------------------------------------------------------
	//Modify points based on chip configuration=------------------------------

	// Most points have a primary and secondary link and can be moved on either
	// Two complete point arrays are required to specify possible chip configurations 

	// BB, Rear Wheel, Shock 1, Shock 2, Idler
	// p0, p1, p2, p3, p11 - Primary Only
	let p0 = modPoint(initPoints, 0, 'primary', chipConfig);
	let p1 = modPoint(initPoints, 1, 'primary', chipConfig);
	let p2 = modPoint(initPoints, 2, 'primary', chipConfig);
	let p3 = modPoint(initPoints, 3, 'primary', chipConfig);
	let p11 = modPoint(initPoints, 11, 'primary', chipConfig);
	let p14 = copy(initPoints[14]);
	let p15 = copy(initPoints[15]);

	//Must handle size specific points separately
	const sizedPoints = geo[Object.keys(geo)[0]].map((_, size) => {
		const sizeSpecPoint = [
			[geo.bbOffset[size], 0], //bb
			[-geo.rc[size] + geo.bbOffset[size], geo.bbDrop[size]], //rear wheel
		]

		return [
			modPoint(sizeSpecPoint, 0, 'primary', chipConfig),
			modPoint(sizeSpecPoint, 1, 'primary', chipConfig)
		];
	});

	//Points will be different depending on which link we ares solving
	// p4 - Frame Primary, L1 Secondary
	// p5 - L1 Primary, L2 Secondary
	// p6 - L2 Primary, L3 Secondary
	// p7 - L3 Primary, Frame Secondary

	// Fix frame initPoints to frame coordinate system and solve linkage to fit
	let p7_l0 = modPoint(initPoints, 7, 'secondary', chipConfig);
	let p4_l0 = modPoint(initPoints, 4, 'primary', chipConfig);

	// Each links initial angles will be taken from initial position + offsets
	// Once angle differences are found the associated points can be rotated as needed
	let p4_l1 = modPoint(initPoints, 4, 'secondary', chipConfig)
	let p5_l1 = modPoint(initPoints, 5, 'primary', chipConfig);

	let p5_l2 = modPoint(initPoints, 5, 'secondary', chipConfig);
	let p6_l2 = modPoint(initPoints, 6, 'primary', chipConfig);

	let p6_l3 = modPoint(initPoints, 6, 'secondary', chipConfig);
	let p7_l3 = modPoint(initPoints, 7, 'primary', chipConfig);

	// p8 - Alt Primary, L4 Secondary
	// p9 - L4 Primary, L5 Secondary
	// p10 - L5 Primary, Alt Secondary
	let p8_la = modPoint(initPoints, 8, 'primary', chipConfig);

	let p8_l4 = modPoint(initPoints, 8, 'secondary', chipConfig);
	let p9_l4 = modPoint(initPoints, 9, 'primary', chipConfig);

	let p9_l5 = modPoint(initPoints, 9, 'secondary', chipConfig);
	let p10_l5 = modPoint(initPoints, 10, 'primary', chipConfig);

	let p10_la = modPoint(initPoints, 10, 'secondary', chipConfig);

	// p12 - Brake 1 Primary, Brake 2 Secondary
	// p13 - Brake 2 Primary, Alt Secondary
	let p12_l6 = modPoint(initPoints, 12, 'primary', chipConfig);

	let p12_l7 = modPoint(initPoints, 12, 'secondary', chipConfig);
	let p13_l7 = modPoint(initPoints, 13, 'primary', chipConfig);

	let p13_la = modPoint(initPoints, 13, 'secondary', chipConfig);


	//Must create virtual points for slider elements.
	//No chips impact p6 and p7 for slider type 1
	//Only chip for p6 on l2 changes the geo for slider type 2 (frame)

	let p6_original = [];
	let p7_original = [];

	if (sliderRef == 1) {
		//Ignore all l3 chips to determine slider angle
		const slider_angle = theta_x(initPoints[7], initPoints[6]) - Math.PI / 2; //Normal line

		//Temporarily place p6 as long distance away on l3
		const new_p6 = [
			Math.cos(slider_angle) * 10000 + initPoints[7][0],
			Math.sin(slider_angle) * 10000 + initPoints[7][1]
		];

		p6_original = copy(initPoints[6]); //Store original position to reset at end

		//Allow no changes to points on l3 for rotating slider
		p6_l2 = copy(new_p6);
		p6_l3 = copy(new_p6);
		p7_l0 = copy(initPoints[7]);
		p7_l3 = copy(initPoints[7]);
	}
	const t3_init = theta_x(p7_l0, p6_l3);

	if (sliderRef == 2) {
		//Ignore all l3 chips to determine slider angle
		const slider_angle = theta_x(initPoints[7], initPoints[6]) + Math.PI / 2; //Normal line

		//Temporarily place new p7 as long distance pivot
		const new_p7 = [
			Math.cos(slider_angle) * 20000 + initPoints[6][0],
			Math.sin(slider_angle) * 20000 + initPoints[6][1]
		];
		p7_original = copy(initPoints[7]); //Store position on frame for reference at the end

		//Allow no changes to l3 points for frame slider except p6 on l2
		p6_l3 = copy(initPoints[6]); //Insure no changes to p6 on l3(frame)
		p7_l0 = copy(new_p7);
		p7_l3 = copy(new_p7);
	}

	//Get all link lengths using the points on the links
	let r0 = magnitude(p4_l0, p7_l0);
	let r1 = magnitude(p4_l1, p5_l1);
	let r2 = magnitude(p5_l2, p6_l2);
	let r3 = magnitude(p6_l3, p7_l3);

	let r4 = magnitude(p8_l4, p9_l4);
	let r5 = magnitude(p9_l5, p10_l5);

	let r6 = magnitude(p1, p12_l6);
	let r7 = magnitude(p12_l7, p13_l7);

	// Get initial angles
	let t0 = theta_x(p7_l0, p4_l0);
	let t1 = theta_x(p4_l1, p5_l1);
	let t2 = theta_x(p5_l2, p6_l2);
	let t3 = theta_x(p6_l3, p7_l3);

	let t4 = theta_x(p8_l4, p9_l4);
	let t5 = theta_x(p9_l5, p10_l5);

	//------------------------------------------------------------------------
	//Initialize first solve parameters and solve-----------------------------
	let direction = false;
	let d_t1 = 0;
	let d_t2 = 0;
	let d_t3 = 0;
	let d_t4 = 0;
	let d_t5 = 0;

	let p5_new = [];
	let p6_new = [];
	let p8_new = [];
	let p9_new = [];
	let p10_new = [];
	let p12_new = [];
	let d_p5 = [0, 0]
	let d_p8 = [0, 0];
	let d_p10 = [0, 0];

	if (solverInfo.linkageType > 0) {
		//Compute the 4 bar loop closure with no d_t1 or d_t3 to find positions
		[d_t1, d_t2, d_t3, direction] = computeFourBar(r0, r1, r2, r3, t0, t1, t2, t3, 0, 0, direction);
	}

	let p4_new = p4_l0;
	let p7_new = p7_l0;
	let d_p4 = subLi(p4_l0, p4_l1); //Translation to make link coaxial
	let d_p7 = subLi(p7_l0, p7_l3); //Translation to make link coaxial

	//Move points for 4 bar linkage
	if (linkageType > 0) {
		p5_new = movePoint(p5_l1, d_p4); //Move the link p5 so that p4 is coaxial
		p5_new = rotPoint(p5_new, p4_new, d_t1); //Rotate p5 to meet loop closure

		d_p5 = subLi(p5_new, p5_l2); //Change in l2 position. This will also comp for coaxial changes
		p6_new = rotMovePoint(p6_l2, p5_l2, d_t2, d_p5);
	} else {
		//If not a 4 bar then points do not matter
		p5_new = p5_l1;
		p6_new = p6_l2;
	}

	//Move points for 6 bar linkage
	if (linkageType == 2) {
		// find p8 based on link attachment-------------------------------
		// The la attachement point is moved-------------------------------
		if (link4AttachedTo == 0) {
			p8_new = p8_la; //Alt link is static frame in this case
			d_p8 = subLi(p8_la, p8_l4); //Must be made coaxial
		} else if (link4AttachedTo == 1) {
			p8_new = movePoint(p8_la, d_p4); //Movement of l1 to be coaxial
			p8_new = rotPoint(p8_new, p4_new, d_t1); //Rotate according to loop closure

			d_p8 = subLi(p8_new, p8_l4); //Find difference between attachment point and assumed link pos
		} else if (link4AttachedTo == 2) {
			p8_new = rotMovePoint(p8_la, p5_l2, d_t2, d_p5); //Rotate as usual. d_p5 accounts for coaxial move
			d_p8 = subLi(p8_new, p8_l4); //Find difference between attachment point and assumed link pos
		} else if (link4AttachedTo == 3) {
			p8_new = movePoint(p8_la, d_p7); //Move with l3 to be coaxial
			p8_new = rotPoint(p8_new, p7_new, d_t3); //Rotate for loop closure
			d_p8 = subLi(p8_new, p8_l4); //Find difference between attachment point and assumed link pos
		}

		// find p10 based on link attachment------------------------------
		if (link5AttachedTo == 0) {
			p10_new = p10_la; // Frame is considered static
			d_p10 = subLi(p10_la, p10_l5); // Movement to be coaxial still required
		} else if (link5AttachedTo == 1) {
			p10_new = movePoint(p10_la, d_p4); //Movement of l1 to be coaxial
			p10_new = rotPoint(p10_new, p4_new, d_t1); //Rotate for loop closure

			d_p10 = subLi(p10_new, p10_l5); //Find difference between attachment point and assumed link pos
		} else if (link5AttachedTo == 2) {
			p10_new = rotMovePoint(p10_la, p5_l2, d_t2, d_p5); //Rotate as usual. d_p5 accounts for coaxial move 
			d_p10 = subLi(p10_new, p10_l5); //Find difference between attachment point and assumed link pos
		} else if (link5AttachedTo == 3) {
			p10_new = movePoint(p10_la, d_p7); //Move with l3 to be coaxial
			p10_new = rotPoint(p10_new, p7_new, d_t3); //Rotate for loop closure
			d_p10 = subLi(p10_new, p10_l5); //Find difference between attachment point and assumed link pos
		}

		// Find p9 based on two circle intersection and previous point
		p9_new = circleIntersect(p8_new, p10_new, r4, r5, p9_l4);

		d_t4 = theta_x(p8_new, p9_new) - t4;
		d_t5 = theta_x(p9_new, p10_new) - t5;

	} else {
		//Points don't matter for non-6bar
		p8_new = p8_la;
		p9_new = p9_l4;
		p10_new = p10_la;
	}

	// Now use deltaT and deltaP to find all other points-------------
	const points = [p0, p1, p2, p3, p11, p13_la, p14, p15];
	const attachements = [
		bbAttachedTo,
		wheelAttachedTo,
		shock1AttachedTo,
		shock2AttachedTo,
		idlerAttachedTo,
		brakeAttachedTo,
		DAQ1AttachedTo,
		DAQ2AttachedTo
	];
	const points_new = [0, 0, 0, 0, 0, 0, 0, 0];

	for (let j = 0; j < points.length; j++) {
		if (attachements[j] == 0) {
			points_new[j] = points[j]; // Frame is considered static and point is already chipped
		} else if (attachements[j] == 1) {
			points[j] = movePoint(points[j], d_p4); // Account for coaxial move first
			points_new[j] = rotPoint(points[j], p4_new, d_t1); // frame connection
		} else if (attachements[j] == 2) {
			//Must rotate assuming l2 is correct then d_p5 accounts for coaxial move
			points_new[j] = rotMovePoint(points[j], p5_l2, d_t2, d_p5);
		} else if (attachements[j] == 3) {
			points[j] = movePoint(points[j], d_p7); //Account for coaxial move first
			points_new[j] = rotPoint(points[j], p7_new, d_t3); // frame connection
		} else if (attachements[j] == 4) {
			//Must rotate assuming l4 is correct then d_p8 accounts for coaxial move
			points_new[j] = rotMovePoint(points[j], p8_l4, d_t4, d_p8);
		} else if (attachements[j] == 5) {
			//Must rotate assuming l5 is correct then d_p10 accounts for coaxial move
			points_new[j] = rotMovePoint(points[j], p10_l5, d_t5, d_p10);
		}
	}

	if (usesBrakeArm) {
		p12_new = circleIntersect(points_new[1], points_new[5], r6, r7, p12_l6);
	} else {
		//Does not matter for no brake arm
		p12_new = p12_l6;
	}

	// For each size find points.
	const sizedPointsNew = sizedPoints.map(size => {
		return size.map((point, pointNum) => {
			//Do nothing if frame is attachment
			if (attachements[pointNum] == 1) {
				point = movePoint(point, d_p4); // Account for coaxial move first
				point = rotPoint(point, p4_new, d_t1); // frame connection
			} else if (attachements[pointNum] == 2) {
				//Must rotate assuming l2 is correct then d_p5 accounts for coaxial move
				point = rotMovePoint(point, p5_l2, d_t2, d_p5);
			} else if (attachements[pointNum] == 3) {
				point = movePoint(point, d_p7); //Account for coaxial move first
				point = rotPoint(point, p7_new, d_t3); // frame connection
			} else if (attachements[pointNum] == 4) {
				//Must rotate assuming l4 is correct then d_p8 accounts for coaxial move
				point = rotMovePoint(point, p8_l4, d_t4, d_p8);
			} else if (attachements[pointNum] == 5) {
				//Must rotate assuming l5 is correct then d_p10 accounts for coaxial move
				point = rotMovePoint(point, p10_l5, d_t5, d_p10);
			}
			return point; //Return moved point
		});
	});

	let p0_new = points_new[0];
	let p1_new = points_new[1];
	let p2_new = points_new[2];
	let p3_new = points_new[3];
	let p11_new = points_new[4];
	let p13_new = points_new[5];
	let p14_new = points_new[6];
	let p15_new = points_new[7];

	//Create new points array 
	const newPoints = [
		p0_new, p1_new, p2_new, p3_new, p4_new, p5_new, p6_new, p7_new,
		p8_new, p9_new, p10_new, p11_new, p12_new, p13_new, p14_new,
		p15_new, initPoints[16], initPoints[17], initPoints[18], initPoints[19]
	];

	//Nominal will be the currently selected size for which loop closure will be done
	//Need to save angle between nominal and size specific vector
	//Need magnitude of size specific vectors
	const frameRef = 4; //All bike styles have a fixed Point4 pivot

	let wheelRef = 4; //Initally assume wheel is on link 1
	if (wheelAttachedTo === 2) { wheelRef = 6; }
	if (wheelAttachedTo === 3) { wheelRef = 7; }
	if (wheelAttachedTo === 4) { wheelRef = 8; }
	if (wheelAttachedTo === 5) { wheelRef = 10; }

	//One stuffel per size
	//Take angle of vector FROM ref point to wheel and bb for both nom and mod
	//Take mag of vector from rev point to wheel and bb for mod
	const sizedPointRelations = sizedPointsNew.map(size => {
		const bbAngle = -theta_x(newPoints[frameRef], size[0]) + theta_x(newPoints[frameRef], newPoints[0]);

		const bbMag = magnitude(newPoints[frameRef], size[0]);

		const wheelAngle = -theta_x(newPoints[wheelRef], size[1]) + theta_x(newPoints[wheelRef], newPoints[1]);
		const wheelMag = magnitude(newPoints[wheelRef], size[1]);

		return {
			bbMag,
			bbAngle,
			wheelMag,
			wheelAngle
		}
	});

	//------------------------------------------------------------------------
	//Move linkage slightly to determine rotation direction-------------------
	const init_res = .001;

	//Take initial result to determine direction
	const initialResult = fourBarSolve(...newPoints, solverInfo, init_res, .001, .001, false);

	//Calculate initial theta to get res mm of shock travel
	let theta1 = initialResult[3] * initialResult[2]; //Initial d_theta
	let theta3 = initialResult[4] * initialResult[2]; //Altd_theta for folding linkage
	let loopDir = initialResult[5]; //Flag to use alt d_theta

	//-----------------------------------------------------------------------
	//Solve several times with different resolutions to improve accuracy-----
	const res_1 = 1;
	const res_2 = .1;
	const res_3 = .01;
	const res_4 = .001;
	let loopCount = 0;
	let res = moveToTravel(newPoints, solverInfo, shockLength, res_1, theta1, theta3, loopDir);
	loopCount += res.loopCount;
	if (res.error) { chipResults.error = true; }

	//Recalibrate rotations for new resolution
	theta1 = res_2 / res_1 * res.theta1;
	theta3 = res_2 / res_1 * res.theta3;

	res = moveToTravel(res.points, solverInfo, shockLength, res_2, theta1, theta3, res.loopDir);
	loopCount += res.loopCount;
	if (res.error) { chipResults.error = true; }

	//Recalibrate rotations for new resolution
	theta1 = res_3 / res_2 * res.theta1;
	theta3 = res_3 / res_2 * res.theta3;

	res = moveToTravel(res.points, solverInfo, shockLength, res_3, theta1, theta3, res.loopDir);
	loopCount += res.loopCount;
	if (res.error) { chipResults.error = true; }

	//Recalibrate rotations for new resolution
	theta1 = res_4 / res_3 * res.theta1;
	theta3 = res_4 / res_3 * res.theta3;

	res = moveToTravel(res.points, solverInfo, shockLength, res_4, theta1, theta3, res.loopDir);
	loopCount += res.loopCount;
	if (res.error) { chipResults.error = true; }

	const finalSizedPoints = sizedPointRelations.map((size, sizeIdx) => {
		const bbAngle = theta_x(res.points[frameRef], res.points[0]) - size.bbAngle;
		const wheelAngle = theta_x(res.points[wheelRef], res.points[1]) - size.wheelAngle;

		const bbPos = [
			Math.cos(bbAngle) * size.bbMag + res.points[frameRef][0],
			Math.sin(bbAngle) * size.bbMag + res.points[frameRef][1]
		]

		const wheelPos = [
			Math.cos(wheelAngle) * size.wheelMag + res.points[wheelRef][0],
			Math.sin(wheelAngle) * size.wheelMag + res.points[wheelRef][1]
		]

		return {
			bbPos,
			wheelPos
		}
	});


	//------------------------------------------------------------------------
	//Compute geometry point positions----------------------------------------
	chipResults.geometry = chipGeo(geo, res.points, drivetrainInfo, solverInfo, chipConfig, finalSizedPoints);

	//Set front wheel position based on current size
	res.points[19] = chipResults.geometry['p_fw'][bikeData.selectedSize];

	if (sliderRef == 1) {
		const t3_final = theta_x(res.points[7], res.points[6]);
		const p6_result = rotPoint(p6_original, res.points[7], t3_final - t3_init);
		res.points[6] = p6_result;
	}
	if (sliderRef == 2) { res.points[7] = p7_original; }

	//Round all points to nearest .001
	chipResults.points = res.points.map((point, idx) => {
		return [Math.round(point[0] * 1000) / 1000, Math.round(point[1] * 1000) / 1000];
	});

	//Rotate points about bb for solver. Only do for currently selected size
	chipResults.solvePoints = res.points.map(point => {
		return rotPoint(point, [0, 0], chipResults.geometry['wb_angle'][bikeData.selectedSize]);
	});

	//COG will not be rotated with WB
	chipResults.solvePoints[18] = initPoints[18];

	//Save new shock stroke
	chipResults.shockStroke = bikeData.solverInfo.shockStroke + chipConfig['shockStroke'];

	chipResults.drivetrainInfo = {
		...bikeData.drivetrainInfo,
		tireDiameter: chipConfig.rearWheel ? chipConfig.rearWheel : bikeData.drivetrainInfo.tireDiameter,
		tireDiameter_front: chipConfig.frontWheel ? chipConfig.frontWheel : bikeData.drivetrainInfo.tireDiameter_front,
	}
	return chipResults;
}

//Add the correct chip modifier to a point and return
export function modPoint(points, point_num, ref, chipConfig) {
	return [
		points[point_num][0] + chipConfig[ref][point_num][0], //x
		points[point_num][1] + chipConfig[ref][point_num][1] //y
	];
}

//Compute new geo with chip overrides
function chipGeo(geo, points, drivetrainInfo, solverInfo, chipConfig, sizedPoints) {
	const chipGeo = copy(geo); //Create deep copy to override with new geo
	chipGeo['p_fw'] = [];
	chipGeo['wb_angle'] = [];

	const key = Object.keys(geo)[0] //Get the first key to iterate over sizes

	const chipList = ['stemLength', 'stemSpacers', 'barHeight', 'lowerHeadset', 'hta', 'reach', 'forkLength', 'offset'];

	geo[key].forEach((na, sizeIdx) => {
		//Get reference BB and Wheel position per size
		const bbPos = sizedPoints[sizeIdx].bbPos;
		const wheelPos = sizedPoints[sizeIdx].wheelPos;

		//Add offsets to chipGeo for every size
		chipList.forEach(key => {
			chipGeo[key][sizeIdx] += chipConfig[key]
		});

		const totalForkLength = chipGeo['htl'][sizeIdx] + chipGeo['lowerHeadset'][sizeIdx] +
			chipGeo['forkLength'][sizeIdx];

		//Need headtube top, front wheel, and saddle location to compute all other geo
		let p_ht = [chipGeo['reach'][sizeIdx] + chipGeo['bbOffset'][sizeIdx], chipGeo['stack'][sizeIdx]]; //Geo has already been computed
		let p_frontWheel = [
			p_ht[0] + geoForkRun(totalForkLength, chipGeo['offset'][sizeIdx], chipGeo['hta'][sizeIdx]),
			p_ht[1] - geoForkRise(totalForkLength, chipGeo['offset'][sizeIdx], chipGeo['hta'][sizeIdx])
		];
		let p_saddle = [
			chipGeo['bbOffset'][sizeIdx] - cosD(chipGeo['sta_eff'][sizeIdx]) * chipGeo['nsh'][sizeIdx],
			sinD(chipGeo['sta_eff'][sizeIdx]) * chipGeo['nsh'][sizeIdx]
		];

		//Override tire radius if chipConfig paramter is provided
		const r_tire_front = chipConfig.frontWheel ? chipConfig.frontWheel / 2 : drivetrainInfo.tireDiameter_front / 2;
		const r_tire_rear = chipConfig.rearWheel ? chipConfig.rearWheel / 2 : drivetrainInfo.tireDiameter / 2;

		const [wb_f, wb_r] = commonTan(p_frontWheel, wheelPos, r_tire_front, r_tire_rear);

		const wb_angle = theta_x(wb_r, wb_f);
		const wb_angle_deg = wb_angle * 180 / Math.PI;

		//Rotate head tube point and get distance from BB for reach/stack
		p_ht = rotPoint(p_ht, bbPos, -wb_angle);
		p_ht = subLi(p_ht, bbPos);

		//Rotate rear wheel point and get distance from BB for rear center
		let p_rearWheel = rotPoint(wheelPos, bbPos, -wb_angle);
		p_rearWheel = subLi(p_rearWheel, bbPos);

		//Save FW position per size to find p19 as needed
		chipGeo['p_fw'][sizeIdx] = copy(p_frontWheel);

		//Rotate front wheel point to get front center
		p_frontWheel = rotPoint(p_frontWheel, bbPos, -wb_angle);

		//When bb is shifted the effective STA can change from stock config
		const sta_bb_shifted = 180 - theta_x(bbPos, p_saddle) * 180 / Math.PI;
		const sta_change = sta_bb_shifted - chipGeo['sta_eff'][sizeIdx] + wb_angle_deg;

		chipGeo['wb_angle'][sizeIdx] = -wb_angle; //Reverse to rotate points in correct dir
		chipGeo['wheelbase'][sizeIdx] = magnitude(wb_f, wb_r);
		chipGeo['reach'][sizeIdx] = p_ht[0];
		chipGeo['stack'][sizeIdx] = p_ht[1];
		chipGeo['sta_eff'][sizeIdx] += sta_change;
		chipGeo['sta_act'][sizeIdx] += wb_angle_deg;
		chipGeo['hta'][sizeIdx] += wb_angle_deg;
		chipGeo['cs'][sizeIdx] = magnitude(bbPos, wheelPos);

		chipGeo['bbHeight'][sizeIdx] = pointToLine(wb_f, wb_r, bbPos);
		chipGeo['bbDrop'][sizeIdx] = r_tire_rear - chipGeo['bbHeight'][sizeIdx];

		chipGeo['rc'][sizeIdx] = Math.abs(p_rearWheel[0]);
		chipGeo['fc'][sizeIdx] = p_frontWheel[0];

		chipGeo['trail'][sizeIdx] = geoTrail(r_tire_front * 2, chipGeo['offset'][sizeIdx], chipGeo['hta'][sizeIdx]);
		chipGeo['htt'][sizeIdx] = geoHTT(chipGeo['reach'][sizeIdx], chipGeo['stack'][sizeIdx], chipGeo['sta_eff'][sizeIdx]);

		const totalSpacers = chipGeo['upperHeadset'][sizeIdx] + chipGeo['stemSpacers'][sizeIdx] + chipGeo['stemStack'][sizeIdx];
		chipGeo['seatedFit'][sizeIdx] = geoSeatedFit(chipGeo['reach'][sizeIdx], chipGeo['stack'][sizeIdx],
			chipGeo['hta'][sizeIdx], chipGeo['stemLength'][sizeIdx], totalSpacers, chipGeo['barHeight'][sizeIdx],
			chipGeo['sta_eff'][sizeIdx], chipGeo['nsh'][sizeIdx]);
	});

	return chipGeo;
}

//Move linkage points to target shock length 
function moveToTravel(points, solverInfo, targetShockLength,
	res, theta1, theta3, loopDir) {
	let shockTravel = targetShockLength - magnitude(points[2], points[3]);
	solverInfo.usesPullShock = shockTravel > 0 ? 1 : 0;
	let shockDir = solverInfo.usesPullShock ? -1 : 1;

	//Use magnitude to determine number of loops
	shockTravel = Math.round(10000 * Math.abs(shockTravel)) / 10000;

	let tempPoints = copy(points); //Initialize point storage

	let loopCount = 0;
	let shockLengthArr = [];
	let isInvalidConfig = false;

	for (let i = 0; i <= shockTravel / res; i++) {
		const tempResult = fourBarSolve(...tempPoints, solverInfo, res, theta1, theta3, loopDir);

		shockLengthArr[i] = magnitude(tempResult[1][2], tempResult[1][3]);

		//Check for shock reversal
		if (i !== 0) {
			if ((shockLengthArr[i] - shockLengthArr[i - 1]) * shockDir > 0) { isInvalidConfig = true; }
		}

		theta1 = tempResult[3]; //Next d_theta
		theta3 = tempResult[4]; //Alternate d_theta for linkage folding
		loopDir = tempResult[5]; //Flag to use alternate d_theta

		//Check for NaN point values and break
		let breakNow = false;
		tempResult[1].forEach((item) => {
			if (isNaN(item[0]) || isNaN(item[1])) { isInvalidConfig = true; }
		});

		if (isInvalidConfig) { break; }
		tempPoints = tempResult[1]; //Save points for next iteration
		loopCount += 1;
	}
	return {
		points: tempPoints,
		loopCount,
		error: isInvalidConfig,
		theta1,
		theta3,
		loopDir,
	}
}
